import { useForm } from "react-hook-form";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "./hooks/useAuth";
import ForgotPassword from "./ForgotPw";

const Login = ({forgotPassHandler, registerHandler}) => {
    const [apiError, setApiError] = useState();
    const { setAuth } = useAuth();
    const [forgotPw, setForgot] = useState(false);
    const [error, setError] = useState()
    const [isLoading, setIsLoading] = useState(false);
    let navigate = useNavigate();
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm();

    const getUserData = async (token) => {
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          let userData = await response.json();
          userData["token"] = token;
          setAuth(userData);
          setApiError(null);
          localStorage.setItem("token", token);
        }
      };
    

    const onFormSubmit = async (data) => {
        setIsLoading(true);
        setApiError(null);
        setError(null);
        
        try {
          const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          });
          
          if (response.ok) {
            const token = await response.json();
            await getUserData(token["token"]);
          } else {
            const errorResponse = await response.json();
            const detail = errorResponse["detail"];
            if (typeof detail === "string") {
              setApiError(detail);
            } else {
              setApiError(detail[0]["ctx"]["reason"]);
            }
            setAuth({});
          }
        } catch (error) {
          setError("Problems with server, check later");
        } finally {
          setIsLoading(false);
        }
      };

      const onErrors = (errors) => console.error(errors);
      
      if (forgotPw) {
        return <ForgotPassword successHandler={(state) => setForgot(state)} />
      }

      return (
        <div className="w-full space-y-4">
          {/* Guest Access Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 text-center">
            <button 
              onClick={() => { setAuth({'email': 'guest', 'username': 'guest'}) }} 
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md text-sm"
            >
              Continue as Guest
            </button>
            <div className="mt-2 space-y-1">
              <p className="text-xs font-medium text-gray-700">Guest Access Includes:</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-green-600">
                <div className="flex items-center justify-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Library Access
                </div>
                <div className="flex items-center justify-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Pitch Analysis
                </div>
                <div className="flex items-center justify-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Intensity Analysis
                </div>
                <div className="flex items-center justify-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Visual Feedback
                </div>
              </div>
              <p className="text-xs text-amber-600 font-medium">No detailed pronunciation assessment</p>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500 text-xs">Full access sign in</span>
            </div>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <form onSubmit={handleSubmit(onFormSubmit, onErrors)} className="space-y-3">
              {/* Email/Username Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email or Username
                </label>
                <input
                  type="text"
                  placeholder="Enter email or username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="username"
                  {...register("email", { 
                    required: "Email or username is required",
                    minLength: {
                      value: 3,
                      message: "Must be at least 3 characters"
                    }
                  })}
                />
                {errors?.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="current-password"
                  {...register("password", { required: "Password is required" })}
                />
                {errors?.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Login Button */}
              <button 
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-md flex items-center justify-center text-sm"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </>
                ) : (
                  "Sign In to Full Features"
                )}
              </button>
            </form>

            {/* Forgot Password */}
            <div className="mt-3 text-center">
              <button 
                onClick={() => { forgotPassHandler(true) }}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors duration-200"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {/* Create Account */}
          <div className="text-center">
            <div className="mb-3">
              <p className="text-gray-600 text-xs mb-2">New to pronunciation practice?</p>
              <button 
                onClick={() => { registerHandler(true) }}
                className="w-full bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 border border-gray-300 text-sm"
              >
                Create New Account
              </button>
            </div>
            
            {/* Full Account Benefits */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100">
              <div className="flex items-center justify-center mb-2">
                <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mr-2 shadow-sm">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Full Access Includes</h3>
              </div>
              
              {/* Pronunciation Analysis Section */}
              <div className="mb-2 p-2 bg-white rounded-lg border border-green-200">
                <h4 className="text-xs font-semibold text-green-700 mb-1 text-center">Detailed Pronunciation Analysis</h4>
                <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Phoneme Level
                  </div>
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Sentence Level
                  </div>
                </div>
              </div>
              
              {/* Additional Features */}
              <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Progress Tracking
                </div>
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Statistics
                </div>
              </div>
              
              {/* Experimental Features */}
              <div className="mt-2 pt-2 border-t border-green-200">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center mr-1">
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-purple-700">Experimental Features</span>
                </div>
                <p className="text-xs text-purple-600 text-center">Early access to new tools & analysis</p>
              </div>
              
              <p className="text-xs text-green-600 font-medium mt-2 text-center">+ All Guest Features</p>
            </div>
          </div>

          {/* Error Display */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current flex-shrink-0 h-4 w-4 text-red-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-700 text-xs font-medium">{apiError}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
              <span className="text-red-700 text-xs font-medium">{error}</span>
            </div>
          )}
        </div>
      );
    };
    
    export default Login;